import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import newspapersService from '../services/newspapers.service';
import { Newspaper } from '../types';
import { NewspapersTable, NewspapersToolbar } from '../components/newspapers/NewspapersTable';
import { NewspapersForm } from '../components/newspapers/NewspapersForm';

export default function NewspapersPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState<number | undefined>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Newspaper | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const { data: newspapersData, isLoading } = useQuery({
    queryKey: ['newspapers', { page, limit, search, yearFilter }],
    queryFn: () => newspapersService.getList({ page, limit, search, year: yearFilter }),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Newspaper, 'id' | 'createdAt' | 'updatedAt'>) => newspapersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newspapers'] });
      setIsFormOpen(false);
      setEditingItem(null);
      setNotification({ message: 'Журнал/газета добавлен(а)', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при создании', type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Newspaper> }) => newspapersService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newspapers'] });
      setIsFormOpen(false);
      setEditingItem(null);
      setNotification({ message: 'Журнал/газета обновлен(а)', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при обновлении', type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => newspapersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newspapers'] });
      setNotification({ message: 'Журнал/газета удален(а)', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при удалении', type: 'error' }),
  });

  const filteredItems = newspapersData?.data || [];
  const total = newspapersData?.pagination?.total || 0;

  const handleCreate = () => { setEditingItem(null); setIsFormOpen(true); };
  const handleEdit = (item: Newspaper) => { setEditingItem(item); setIsFormOpen(true); };
  const handleSubmit = async (data: any) => {
    if (editingItem) await updateMutation.mutateAsync({ id: editingItem.id, data });
    else await createMutation.mutateAsync(data);
  };
  const handleDelete = (id: number) => { if (window.confirm('Удалить журнал/газету?')) deleteMutation.mutate(id); };
  const handleCloseNotification = () => setNotification(null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>Журналы и газеты</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>Добавить</Button>
      </Box>
      <NewspapersToolbar 
        search={search} 
        onSearchChange={(value) => { setSearch(value); setPage(1); }} 
        yearFilter={yearFilter}
        onYearFilterChange={(value) => { setYearFilter(value); setPage(1); }}
      />
      <NewspapersTable 
        items={filteredItems} 
        loading={isLoading} 
        total={total} 
        page={page} 
        limit={limit} 
        onPageChange={setPage} 
        onEdit={handleEdit} 
        onDelete={handleDelete} 
      />
      <NewspapersForm 
        open={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
        onSubmit={handleSubmit} 
        initialData={editingItem as any} 
        loading={createMutation.isPending || updateMutation.isPending} 
        error={createMutation.error?.message || updateMutation.error?.message} 
      />
      <Snackbar open={!!notification} autoHideDuration={3000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseNotification} severity={notification?.type === 'success' ? 'success' : 'error'} sx={{ width: '100%' }} icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}